import React from 'react'
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Alert,
  Typography,
  Link,
} from '@mui/material'
import { KYC_STATUS } from '../../utils/kycApprovalUtils'

/**
 * KYC Section Component
 * Handles document verification and status
 */
export default function KycSection({ data, onChange, disabled, loading, error }) {
  const handleChange = (field, value) => {
    onChange({
      ...data,
      [field]: value,
    })
  }

  const handleChecklistChange = (field, value) => {
    onChange({
      ...data,
      checklist: {
        ...data.checklist,
        [field]: value,
      },
    })
  }

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        KYC Document Verification
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Verify therapist documents and set KYC status
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'grid', gap: 3 }}>
        {/* KYC Status */}
        <FormControl fullWidth>
          <InputLabel>KYC Status</InputLabel>
          <Select
            value={data.status || KYC_STATUS.PENDING}
            onChange={(e) => handleChange('status', e.target.value)}
            disabled={disabled || loading}
            label="KYC Status"
          >
            <MenuItem value={KYC_STATUS.APPROVED}>Approved</MenuItem>
            <MenuItem value={KYC_STATUS.PENDING}>Pending</MenuItem>
            <MenuItem value={KYC_STATUS.REJECTED}>Rejected</MenuItem>
          </Select>
        </FormControl>

        {/* Notes */}
        <TextField
          label="Notes / Remarks"
          multiline
          rows={3}
          value={data.notes || ''}
          onChange={(e) => handleChange('notes', e.target.value)}
          disabled={disabled || loading}
          fullWidth
          placeholder="Add any clarifications or notes here..."
        />

        {/* Document Checklist */}
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Document Verification Checklist
          </Typography>
          <Box sx={{ display: 'grid', gap: 1 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={data.checklist?.govtId || false}
                  onChange={(e) => handleChecklistChange('govtId', e.target.checked)}
                  disabled={disabled || loading}
                />
              }
              label="Government ID Verified (Aadhaar/PAN)"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={data.checklist?.degreeCert || false}
                  onChange={(e) => handleChecklistChange('degreeCert', e.target.checked)}
                  disabled={disabled || loading}
                />
              }
              label="Degree Certificate Verified"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={data.checklist?.councilReg || false}
                  onChange={(e) => handleChecklistChange('councilReg', e.target.checked)}
                  disabled={disabled || loading}
                />
              }
              label="Council Registration Verified (RCI/MCI)"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={data.checklist?.addressProof || false}
                  onChange={(e) => handleChecklistChange('addressProof', e.target.checked)}
                  disabled={disabled || loading}
                />
              }
              label="Address Proof Verified"
            />
          </Box>
        </Box>

        {/* Document URLs (read-only for now) */}
        {data.documents && (
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Uploaded Documents
            </Typography>
            <Box sx={{ display: 'grid', gap: 1 }}>
              {data.documents.govtIdUrls?.length > 0 && (
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Government ID:
                  </Typography>
                  {data.documents.govtIdUrls.map((url, i) => (
                    <Link key={i} href={url} target="_blank" rel="noopener" sx={{ display: 'block', ml: 2 }}>
                      View Document {i + 1}
                    </Link>
                  ))}
                </Box>
              )}
              {data.documents.degreeCertificateUrls?.length > 0 && (
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Degree Certificate:
                  </Typography>
                  {data.documents.degreeCertificateUrls.map((url, i) => (
                    <Link key={i} href={url} target="_blank" rel="noopener" sx={{ display: 'block', ml: 2 }}>
                      View Document {i + 1}
                    </Link>
                  ))}
                </Box>
              )}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  )
}
